import styles from '@hts/entities/Card/styles/InfoContent/TextFields/TextField.module.scss';
import { INewFieldItem } from '@hts/entities/Card/types';
import { ELanguageType } from '@hts/shared/enums';
import { HtsButtonIcon, HtslIcon, TextInput } from '@hts/shared/ui';
import { memo, useCallback, useRef } from 'react';

interface ITextFieldProps {
  item: INewFieldItem;
  focused: boolean;
  onChangeText: (id: string, value: string, originalInput: boolean) => void;
  onFocus: (id: string) => void;
  onClickReset: (id: string) => void;
  onClickBackspace: (id: string) => void;
  onBlur: (id: string) => void;
  toggleKeyboardView: (e: React.MouseEvent<HTMLElement>, id: string, keyboard: string) => void;
}

export const TextField: React.FC<ITextFieldProps> = memo(
  ({
    item,
    focused,
    onChangeText,
    onFocus,
    onClickReset,
    onClickBackspace,
    onBlur,
    toggleKeyboardView,
  }) => {
    const { id, text, defaultText, isHidden, text_lang } = item;

    const inputRef = useRef<HTMLInputElement>(null);

    const isDefaultText = text === defaultText;
    const direction = text_lang === ELanguageType.HEBREW ? 'rtl' : 'ltr';

    const handleChangeText = useCallback(
      (value: string) => {
        onChangeText(id, value, true);
      },
      [id, onChangeText]
    );

    const handleFocus = useCallback(
      (e: React.ChangeEvent<HTMLInputElement>) => {
        onFocus(id);
      },
      [id, onFocus]
    );

    const handleSelect = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {}, []);

    const handleBlur = useCallback(
      (e: React.FocusEvent<HTMLInputElement>) => {
        onBlur(id);
      },
      [id, onBlur]
    );

    const handleReset = useCallback(() => {
      onClickReset(id);
    }, [id, onClickReset]);

    const handleBackspace = useCallback(() => {
      onClickBackspace(id);
    }, [id, onClickBackspace]);

    const onClickKeyboard = useCallback(
      (e: React.MouseEvent<HTMLButtonElement>) => {
        if (inputRef.current) {
          inputRef.current.focus();
        }

        toggleKeyboardView(e, id, text_lang);
      },
      [toggleKeyboardView, id, text_lang]
    );

    return (
      <TextInput
        ref={inputRef}
        value={text}
        fullWidth
        leftContent={
          <div className={styles.leftButtonsContainer}>
            <HtsButtonIcon
              title="Reset"
              className={styles.buttonContainer}
              onClick={isDefaultText ? handleBackspace : handleReset}
            >
              <HtslIcon
                icon={isDefaultText ? 'keyboard-backspace' : 'restore'}
                color="primaryDark"
                style={{ fontSize: isDefaultText ? 20 : 16 }}
              />
            </HtsButtonIcon>
          </div>
        }
        rightContent={
          <HtsButtonIcon
            title="Keyboard"
            className={styles.buttonContainer}
            onClick={onClickKeyboard}
          >
            <HtslIcon icon="keyboard-new" color="gray7" />
          </HtsButtonIcon>
        }
        containerProps={{
          className: `${styles.container} ${isHidden ? styles.hiddenContainer : ''} ${focused ? styles.focusedContainer : ''}`,
        }}
        inputProps={{
          id,
          dir: direction,
          onFocus: handleFocus,
          onBlur: handleBlur,
          onSelect: handleSelect,
        }}
        onChangeValue={handleChangeText}
      />
    );
  }
);
